<template>
  <b-modal
    ref="modal"
    :title="modalTitle"
    size="lg"
    hide-footer
    @hidden="handleHidden"
  >
    <rc-overlay :show="loading || saving || backlogLoading">
      <div v-if="isTeamDefinitionContext && !selectedOrder">
        <b-form-group
          :label="$t('order.planning.order-search')"
          label-for="orderPlanningSearch"
        >
          <b-input-group>
            <b-form-input
              id="orderPlanningSearch"
              v-model.trim="orderSearch"
              :placeholder="$t('order.planning.order-search-placeholder')"
              @input="handleOrderSearchInput"
            />
            <b-input-group-append>
              <b-button
                variant="outline-secondary"
                @click="fetchBacklogOrders"
              >
                {{ $t('order.planning.refresh') }}
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>

        <b-table
          small
          responsive
          hover
          :items="backlogOrders"
          :fields="backlogColumns"
          primary-key="id"
          show-empty
          :empty-text="$t('order.planning.empty-backlog')"
        >
          <template #cell(fullNumber)="data">
            <div class="text-center text-nowrap">
              <b-badge
                variant="light-secondary"
                class="font-medium-1 planning-order-number-badge"
              >
                {{ data.item.fullNumber || '-' }}
              </b-badge>
            </div>
          </template>

          <template #cell(flags)="data">
            <div class="d-flex align-items-center justify-content-center flex-wrap">
              <b-avatar
                v-if="data.item.prioritySignals"
                v-b-tooltip.hover.top="$t('order.priority-signals.true')"
                size="28"
                variant="primary"
                class="m-25"
              >
                <feather-icon
                  icon="LoaderIcon"
                  class="font-medium-4"
                />
              </b-avatar>
              <b-avatar
                v-if="data.item.absolutePunctuality"
                v-b-tooltip.hover.top="$t('order.label.base-order-info.absolute-punctuality')"
                size="28"
                variant="danger"
                class="m-25"
              >
                <feather-icon
                  icon="ClockIcon"
                  class="font-medium-4"
                />
              </b-avatar>
              <b-avatar
                v-if="data.item.infectious"
                v-b-tooltip.hover.top="$t('order.label.infectious')"
                size="28"
                variant="warning"
                class="m-25"
              >
                <feather-icon
                  icon="AlertTriangleIcon"
                  class="font-medium-4"
                />
              </b-avatar>
              <b-avatar
                v-if="data.item.private"
                v-b-tooltip.hover.top="$t('order.label.private-order')"
                size="28"
                variant="success"
                class="m-25"
              >
                <feather-icon
                  icon="DollarSignIcon"
                  class="font-medium-4"
                />
              </b-avatar>
            </div>
          </template>

          <template #cell(route)="data">
            <div class="text-center">
              <feather-icon
                :id="getBacklogRoutePopoverTarget(data.item)"
                icon="MapIcon"
                class="font-medium-3"
              />
              <b-popover
                :target="getBacklogRoutePopoverTarget(data.item)"
                triggers="hover focus"
                placement="left"
              >
                <div class="planning-route-popover">
                  <div class="planning-route-popover__section">
                    <div class="planning-route-popover__label text-muted">
                      {{ $t('order.label.pickup-address') }}
                    </div>
                    <div
                      v-if="getBacklogAddressParts(data.item.pickupAddress)"
                      class="planning-route-popover__value"
                    >
                      <div v-if="getBacklogAddressParts(data.item.pickupAddress).isAddressPrimary">
                        <div class="font-small-4">
                          {{ getBacklogAddressParts(data.item.pickupAddress).addressLine }}
                        </div>
                      </div>
                      <div
                        v-else
                        :class="{ 'mb-25': getBacklogAddressParts(data.item.pickupAddress).addressLine }"
                      >
                        <span
                          v-if="getBacklogAddressParts(data.item.pickupAddress).alias"
                          class="font-small-4 font-weight-bold d-block"
                        >
                          {{ getBacklogAddressParts(data.item.pickupAddress).alias }}
                        </span>
                        <span
                          v-if="getBacklogAddressParts(data.item.pickupAddress).name"
                          class="text-body d-bloc"
                        >
                          {{ getBacklogAddressParts(data.item.pickupAddress).name }}
                        </span>
                      </div>
                      <small
                        v-if="getBacklogAddressParts(data.item.pickupAddress).addressLine && !getBacklogAddressParts(data.item.pickupAddress).isAddressPrimary"
                        class="text-muted d-block"
                      >
                        {{ getBacklogAddressParts(data.item.pickupAddress).addressLine }}
                      </small>
                    </div>
                    <div
                      v-else
                      class="planning-route-popover__value font-small-4"
                    >
                      {{ $t('shared.no-data') }}
                    </div>
                  </div>
                  <div class="planning-route-popover__section">
                    <div class="planning-route-popover__label text-muted">
                      {{ $t('order.label.delivery-address') }}
                    </div>
                    <div
                      v-if="getBacklogAddressParts(data.item.deliveryAddress)"
                      class="planning-route-popover__value"
                    >
                      <div v-if="getBacklogAddressParts(data.item.deliveryAddress).isAddressPrimary">
                        <div class="font-small-4">
                          {{ getBacklogAddressParts(data.item.deliveryAddress).addressLine }}
                        </div>
                      </div>
                      <div
                        v-else
                        :class="{ 'mb-25': getBacklogAddressParts(data.item.deliveryAddress).addressLine }"
                      >
                        <span
                          v-if="getBacklogAddressParts(data.item.deliveryAddress).alias"
                          class="font-small-4 font-weight-bold d-block"
                        >
                          {{ getBacklogAddressParts(data.item.deliveryAddress).alias }}
                        </span>
                        <span
                          v-if="getBacklogAddressParts(data.item.deliveryAddress).name"
                          class="text-body d-block"
                        >
                          {{ getBacklogAddressParts(data.item.deliveryAddress).name }}
                        </span>
                      </div>
                      <small
                        v-if="getBacklogAddressParts(data.item.deliveryAddress).addressLine && !getBacklogAddressParts(data.item.deliveryAddress).isAddressPrimary"
                        class="text-muted d-block"
                      >
                        {{ getBacklogAddressParts(data.item.deliveryAddress).addressLine }}
                      </small>
                    </div>
                    <div
                      v-else
                      class="planning-route-popover__value font-small-4"
                    >
                      {{ $t('shared.no-data') }}
                    </div>
                  </div>
                </div>
              </b-popover>
            </div>
          </template>

          <template #cell(startAt)="data">
            <div class="text-center text-nowrap">
              <b-badge
                class="font-medium-1"
                variant="light-secondary"
              >
                {{ transformDatetimeToHumanReadablePretty(data.item.startAt) }}
              </b-badge>
            </div>
          </template>

          <template #cell(actions)="data">
            <div class="text-right">
              <b-button
                size="sm"
                variant="outline-primary"
                @click="selectOrder(data.item)"
              >
                {{ $t('order.planning.plan-action') }}
              </b-button>
            </div>
          </template>
        </b-table>

        <div
          v-if="backlogTotal > backlogQuery.limit"
          class="d-flex justify-content-end mb-1"
        >
          <b-pagination
            v-model="backlogQuery.page"
            :total-rows="backlogTotal"
            :per-page="backlogQuery.limit"
            first-number
            last-number
            class="mb-0"
            @input="fetchBacklogOrders"
          />
        </div>

        <div class="d-flex justify-content-start align-items-center flex-wrap">
          <b-button
            variant="outline-secondary"
            class="mb-50 mb-sm-0"
            @click="hide"
          >
            {{ $t('shared.button.cancel') }}
          </b-button>
        </div>
      </div>

      <div v-else-if="selectedOrder">
        <b-card
          no-body
          class="shadow-none border mb-1 mt-50"
        >
          <div class="p-1">
            <div class="d-flex align-items-center justify-content-between flex-wrap mb-25">
              <div class="d-flex align-items-center flex-wrap">
                <router-link
                  :to="{ name: 'order-view', params: { id: selectedOrderId } }"
                  class="font-medium-3 font-weight-bolder mr-50"
                >
                  {{ selectedOrder.fullNumber || '-' }}
                </router-link>
                <div
                  v-if="selectedOrder.returnTransport || selectedOrder.prioritySignals || selectedOrder.absolutePunctuality || selectedOrder.infectious || selectedOrder.private"
                  class="d-flex align-items-center flex-wrap"
                >
                  <b-avatar
                    v-if="selectedOrder.returnTransport"
                    :title="$t('order.label.base-order-info.return-transport')"
                    size="18"
                    variant="info"
                    class="mr-25"
                  >
                    <feather-icon
                      icon="RepeatIcon"
                      size="12"
                    />
                  </b-avatar>
                  <b-avatar
                    v-if="selectedOrder.prioritySignals"
                    title="Przejazd na sygnalach uprzywilejowania"
                    size="18"
                    variant="primary"
                    class="mr-25"
                  >
                    <feather-icon
                      icon="LoaderIcon"
                      size="12"
                    />
                  </b-avatar>
                  <b-avatar
                    v-if="selectedOrder.absolutePunctuality"
                    title="Nie mozna sie spoznic"
                    size="18"
                    variant="danger"
                    class="mr-25"
                  >
                    <feather-icon
                      icon="ClockIcon"
                      size="12"
                    />
                  </b-avatar>
                  <b-avatar
                    v-if="selectedOrder.infectious"
                    title="Zakazny"
                    size="18"
                    variant="warning"
                    class="mr-25"
                  >
                    <feather-icon
                      icon="AlertTriangleIcon"
                      size="12"
                    />
                  </b-avatar>
                  <b-avatar
                    v-if="selectedOrder.private"
                    title="Prywatny"
                    size="18"
                    variant="success"
                    class="mr-25"
                  >
                    <feather-icon
                      icon="DollarSignIcon"
                      size="12"
                    />
                  </b-avatar>
                </div>
              </div>
              <b-badge
                variant="light-primary"
                class="font-small-2 px-75 py-50"
              >
                {{ transformDatetimeToHumanReadablePretty(selectedOrder.startAt) }}
              </b-badge>
            </div>

            <div
              v-if="selectedOrder.customerName"
              class="d-flex align-items-center text-muted font-small-3 mt-25"
            >
              <feather-icon
                icon="BriefcaseIcon"
                size="13"
                class="mr-25 flex-shrink-0"
              />
              <span class="text-truncate">{{ selectedOrder.customerName }}</span>
            </div>

            <div
              v-if="selectedOrderPatients.length"
              class="d-flex align-items-center text-muted font-small-3 mt-25"
            >
              <feather-icon
                icon="UserIcon"
                size="13"
                class="mr-25 flex-shrink-0"
              />
              <span class="text-truncate">{{ selectedOrderPatients }}</span>
            </div>

            <div
              v-if="selectedOrder.pickupAddress || selectedOrder.deliveryAddress"
              class="mt-50"
            >
              <div class="d-md-flex align-items-baseline font-small-3 text-muted">
                <feather-icon
                  icon="ArrowUpCircleIcon"
                  size="13"
                  class="mr-25 flex-shrink-0 text-success"
                />
                <span class="mr-50">{{ formatOrderAddress(selectedOrder.pickupAddress) }}</span>
                <feather-icon
                  icon="ArrowDownCircleIcon"
                  size="13"
                  class="mr-25 flex-shrink-0 text-danger"
                />
                <span>{{ formatOrderAddress(selectedOrder.deliveryAddress) }}</span>
              </div>
            </div>
          </div>
        </b-card>

        <b-alert
          v-if="plannerDropTimeWarning"
          show
          variant="danger"
          class="p-1"
        >
          <div class="mb-1 whitespace-pre-line">
            {{ plannerDropTimeWarning }}
          </div>

          <b-form-radio-group
            v-model="plannerDropStartAtMode"
            :options="plannerDropStartAtOptions"
            stacked
          />
        </b-alert>

        <b-row>
          <b-col cols="12">
            <b-form-group
              :label="$t('order.planning.team-definition')"
              label-for="orderPlanningTeamDefinitions"
            >
              <v-select
                id="orderPlanningTeamDefinitions"
                v-model="selectedTeamDefinitionIds"
                :options="teamDefinitionOptions"
                :clearable="false"
                :append-to-body="true"
                :calculate-position="calculateDurationSelectPosition"
                label="text"
                :reduce="option => option.value"
                multiple
                :placeholder="$t('order.planning.select-team-definition')"
              >
                <template #no-options>
                  {{ firstTeamIncompatibleWarning ? $t(firstTeamIncompatibleWarning) : $t('order.planning.first-available-no-definitions') }}
                </template>
              </v-select>
              <small
                v-if="firstAvailableSelectionError"
                class="text-danger d-block mt-50"
              >
                {{ firstAvailableSelectionError }}
              </small>
            </b-form-group>
            <b-alert
              :show="isFirstAvailable && !firstAvailableSelectionError"
              variant="info"
              class="mb-1 p-1"
            >
              <feather-icon
                icon="InfoIcon"
                size="14"
                class="mr-50"
              />
              {{ $t('order.planning.first-available-info') }}
            </b-alert>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              label-for="orderPlanningAssignmentOffset"
            >
              <template #label>
                <span class="d-inline-flex align-items-center">
                  <span>{{ $t('order.planning.assignment-at') }}</span>
                  <feather-icon
                    :id="assignmentHelpTooltipId"
                    v-b-tooltip.hover.top="$t('order.planning.assignment-help')"
                    icon="InfoIcon"
                    size="14"
                    class="text-muted ml-25 cursor-help"
                  />
                </span>
              </template>
              <v-select
                id="orderPlanningAssignmentOffset"
                :value="assignmentOffsetInput"
                :options="assignmentOffsetOptions"
                :clearable="false"
                :append-to-body="true"
                :calculate-position="calculateDurationSelectPosition"
                taggable
                push-tags
                :create-option="value => value"
                :placeholder="$t('order.planning.duration-placeholder')"
                @input="handleAssignmentOffsetInput"
              />
              <small
                v-if="assignmentOffsetValidationError"
                class="text-danger d-block mt-50"
              >
                {{ assignmentOffsetValidationError }}
              </small>
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              label-for="orderPlanningDurationMinutes"
            >
              <template #label>
                <span class="d-inline-flex align-items-center">
                  <span>{{ $t('order.planning.duration-minutes') }}</span>
                  <feather-icon
                    :id="durationHelpTooltipId"
                    v-b-tooltip.hover.top="$t('order.planning.duration-help')"
                    icon="InfoIcon"
                    size="14"
                    class="text-muted ml-25 cursor-help"
                  />
                </span>
              </template>
              <v-select
                id="orderPlanningDurationMinutes"
                :value="durationInput"
                :options="durationOptions"
                :clearable="false"
                :append-to-body="true"
                :calculate-position="calculateDurationSelectPosition"
                taggable
                push-tags
                :create-option="value => value"
                :placeholder="$t('order.planning.duration-placeholder')"
                @input="handleDurationInput"
              />
              <small
                v-if="durationValidationError"
                class="text-danger d-block mt-50"
              >
                {{ durationValidationError }}
              </small>
            </b-form-group>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <b-button
            v-if="canRemovePlanning"
            variant="outline-danger"
            class="mb-50 mb-sm-0"
            :disabled="saving"
            @click="removePlanning"
          >
            {{ $t('order.planning.remove-action') }}
          </b-button>
          <div v-else />

          <div class="d-flex align-items-center flex-wrap justify-content-end">
            <b-button
              variant="outline-secondary"
              class="mr-50 mb-50 mb-sm-0"
              @click="hide"
            >
              {{ $t('shared.button.cancel') }}
            </b-button>
            <b-button
              variant="primary"
              class="mb-50 mb-sm-0"
              :disabled="!canSubmit"
              @click="savePlanning"
            >
              {{ $t('shared.button.submit') }}
            </b-button>
          </div>
        </div>
      </div>
    </rc-overlay>
  </b-modal>
</template>

<script>
import moment from 'moment'
import {
  BAlert,
  BPagination,
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormGroup,
  BFormRadioGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BModal,
  BPopover,
  BRow,
  BTable,
  VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import {
  parseRequestError,
  transformDatetimeToHumanReadablePretty,
} from '@/helpers/helpers'
import {
  formatPlanningMinutes,
  getPlanningAssignmentOffsetOptions,
  normalizePlanningTimeInput,
  PLANNING_TIME_SUGGESTIONS_MINUTES,
} from '@/helpers/orderPlanning'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import RcOverlay from '@/layouts/components/rc/RcOverlay.vue'
import { orderRequests } from '@/http/requests/order/order'
import teamDefinitionRequests from '@/http/requests/settings/team-definition/team-definition'

const ORDER_SEARCH_DEBOUNCE_MS = 300
const BACKLOG_LIMIT = 10
export default {
  name: 'OrderPlanningModal',
  components: {
    BPagination,
    BAlert,
    BAvatar,
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormGroup,
    BFormRadioGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BModal,
    BPopover,
    BRow,
    BTable,
    FeatherIcon,
    RcOverlay,
    vSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      loading: false,
      saving: false,
      backlogLoading: false,
      mode: null,
      fixedTeamDefinition: null,
      selectedOrder: null,
      selectedTeamDefinitionIds: [],
      assignmentOffsetMinutes: null,
      assignmentOffsetInput: null,
      assignmentOffsetValidationError: '',
      durationMinutes: null,
      durationInput: null,
      durationValidationError: '',
      plannerDropContext: null,
      plannerDropStartAtMode: 'drop',
      teamDefinitions: [],
      backlogOrders: [],
      backlogQuery: {
        limit: BACKLOG_LIMIT,
        page: 1,
      },
      backlogTotal: 0,
      orderSearch: '',
      orderSearchTimeout: null,
      backlogColumns: [
        {
          key: 'fullNumber',
          label: this.$t('order.label.full-number'),
          class: 'text-center align-middle',
          thClass: 'text-center align-middle',
        },
        {
          key: 'flags',
          label: '',
          class: 'text-center align-middle planning-flags-column',
          thClass: 'text-center align-middle planning-flags-column',
        },
        {
          key: 'route',
          label: `${this.$t('order.label.pickup-address-short')} / ${this.$t('order.label.delivery-address-short')}`,
          class: 'text-center align-middle planning-route-column',
          thClass: 'text-center align-middle planning-route-column',
        },
        {
          key: 'startAt',
          label: this.$t('order.label.start-date'),
          class: 'text-center align-middle',
          thClass: 'text-center align-middle',
        },
        {
          key: 'actions',
          label: '',
          class: 'text-right align-middle',
          thClass: 'text-right align-middle',
        },
      ],
    }
  },
  computed: {
    selectedOrderPatients() {
      const patients = this.selectedOrder?.patients
      if (!Array.isArray(patients) || patients.length === 0) {
        return ''
      }

      return patients
        .map(p => [p.surname, p.name].filter(Boolean).join(' '))
        .filter(Boolean)
        .join(', ')
    },
    assignmentHelpTooltipId() {
      return 'order-planning-assignment-help'
    },
    durationHelpTooltipId() {
      return 'order-planning-duration-help'
    },
    modalTitle() {
      if (this.isOrderContext) {
        return this.$t('order.planning.order-modal-title')
      }

      if (this.fixedTeamDefinition?.code) {
        return `${this.$t('order.planning.team-modal-title-prefix')} ${this.fixedTeamDefinition.code}`
      }

      return this.$t('order.planning.team-modal-title-prefix')
    },
    isOrderContext() {
      return this.mode === 'order'
    },
    isTeamDefinitionContext() {
      return this.mode === 'teamDefinition'
    },
    isTeamDefinitionLocked() {
      return this.isTeamDefinitionContext && !!(this.fixedTeamDefinition && this.fixedTeamDefinition.id)
    },
    teamDefinitionOptions() {
      if (this.selectedTeamDefinitionIds.length === 0) {
        return this.teamDefinitions.map(td => ({ value: td.id, text: td.code }))
      }
      const firstId = this.selectedTeamDefinitionIds[0]
      const firstTd = this.teamDefinitions.find(td => Number(td.id) === Number(firstId))
      if (!firstTd?.acceptsFirstAvailableOrders) {
        return [{ value: firstTd.id, text: firstTd.code }]
      }
      return this.teamDefinitions.filter(td => td.acceptsFirstAvailableOrders).map(td => ({ value: td.id, text: td.code }))
    },
    firstTeamIncompatibleWarning() {
      if (this.selectedTeamDefinitionIds.length !== 1) return ''
      const firstId = this.selectedTeamDefinitionIds[0]
      const firstTd = this.teamDefinitions.find(td => Number(td.id) === Number(firstId))
      return firstTd && !firstTd.acceptsFirstAvailableOrders
        ? 'order.planning.first-available-first-team-incompatible'
        : ''
    },
    isFirstAvailable() {
      return this.selectedTeamDefinitionIds.length > 1
    },
    firstAvailableSelectionError() {
      if (this.selectedTeamDefinitionIds.length <= 1) return ''
      const hasInvalid = this.selectedTeamDefinitionIds.some(id => {
        const td = this.teamDefinitions.find(t => Number(t.id) === Number(id))
        return td && !td.acceptsFirstAvailableOrders
      })
      return hasInvalid ? this.$t('order.planning.first-available-team-invalid') : ''
    },
    firstAvailableTeamDefinitionOptions() {
      const options = this.teamDefinitions
        .filter(td => td.acceptsFirstAvailableOrders)
        .map(td => ({
          value: td.id,
          text: td.code,
        }))

      this.selectedTeamDefinitionIds.forEach(id => {
        if (options.some(option => Number(option.value) === Number(id))) {
          return
        }

        const teamDefinition = this.teamDefinitions.find(td => Number(td?.id) === Number(id))
          || this.selectedOrder?.plannedTeamDefinition
        if (!teamDefinition || Number(teamDefinition.id) !== Number(id)) {
          return
        }

        options.push({
          value: teamDefinition.id,
          text: teamDefinition.code || String(teamDefinition.id),
        })
      })

      return options
    },
    durationOptions() {
      const options = PLANNING_TIME_SUGGESTIONS_MINUTES.map(this.formatDurationMinutes)

      if (this.durationInput && !options.includes(this.durationInput)) {
        return options.concat(this.durationInput)
      }

      return options
    },
    assignmentOffsetOptions() {
      const options = getPlanningAssignmentOffsetOptions(this.formatDurationMinutes)

      if (this.assignmentOffsetInput && !options.includes(this.assignmentOffsetInput)) {
        return options.concat(this.assignmentOffsetInput)
      }

      return options
    },
    canRemovePlanning() {
      if (!this.selectedOrderId || !this.selectedOrder) {
        return false
      }

      const hasLegacyPlanning = !!(this.selectedOrder.plannedTeamDefinition && this.selectedOrder.plannedTeamDefinition.id)
      const hasFirstAvailablePlanning = !!(this.selectedOrder.isFirstAvailablePlanning)

      return hasLegacyPlanning || hasFirstAvailablePlanning
    },
    selectedOrderId() {
      const orderId = this.selectedOrder?.id ?? this.selectedOrder?.orderId ?? null

      return orderId !== null && orderId !== undefined && orderId !== ''
        ? orderId
        : null
    },
    canSubmit() {
      if (this.saving || !this.selectedOrder || !this.selectedOrderId) {
        return false
      }

      const timingValid = this.assignmentOffsetMinutes !== null
        && !this.assignmentOffsetValidationError
        && !this.durationValidationError
        && Number(this.durationMinutes) > 0

      return timingValid && this.selectedTeamDefinitionIds.length > 0 && !this.firstAvailableSelectionError
    },
    plannerDropTimeWarning() {
      if (!this.hasPlannerDropTimeChange) {
        return ''
      }

      return this.$t('order.planning.planner-drop-time-warning', {
        originalTime: transformDatetimeToHumanReadablePretty(this.selectedOrder.startAt),
        plannedTime: transformDatetimeToHumanReadablePretty(this.plannerDropContext.startAt),
      })
    },
    hasPlannerDropTimeChange() {
      if (!this.plannerDropContext || !this.selectedOrder?.startAt) {
        return false
      }

      const originalStartAt = moment.parseZone(this.selectedOrder.startAt)
      const droppedStartAt = moment.parseZone(this.plannerDropContext.startAt)

      return originalStartAt.isValid()
        && droppedStartAt.isValid()
        && originalStartAt.valueOf() !== droppedStartAt.valueOf()
    },
    plannerDropStartAtOptions() {
      if (!this.hasPlannerDropTimeChange) {
        return []
      }

      return [
        {
          text: this.$t('order.planning.planner-drop-start-at-keep', {
            originalTime: transformDatetimeToHumanReadablePretty(this.selectedOrder.startAt),
          }),
          value: 'keep',
        },
        {
          text: this.$t('order.planning.planner-drop-start-at-drop', {
            plannedTime: transformDatetimeToHumanReadablePretty(this.plannerDropContext.startAt),
          }),
          value: 'drop',
        },
      ]
    },
    shouldUseDroppedStartAt() {
      return this.hasPlannerDropTimeChange && this.plannerDropStartAtMode === 'drop'
    },
  },
  beforeDestroy() {
    clearTimeout(this.orderSearchTimeout)
  },
  methods: {
    transformDatetimeToHumanReadablePretty,
    formatOrderAddress(address) {
      if (!address || typeof address !== 'object') {
        return '-'
      }

      return address.alias || address.name || [address.city, address.address].filter(Boolean).join(', ') || '-'
    },
    async openForOrder(order) {
      this.resetState()
      this.mode = 'order'
      this.loading = true

      try {
        await this.loadTeamDefinitions()
        this.selectOrder(order)
        this.show()
      } catch (error) {
        parseRequestError(error)
      } finally {
        this.loading = false
      }
    },
    async openForPlannerDrop({ order, teamDefinitionId, startAt }) {
      this.resetState()
      this.mode = 'order'
      this.loading = true

      try {
        await this.loadTeamDefinitions()
        this.selectOrder(order)
        const resolvedId = Number(teamDefinitionId || 0) || null
        if (resolvedId) {
          this.selectedTeamDefinitionIds = [resolvedId]
        }
        this.plannerDropContext = {
          startAt: startAt || order?.startAt || null,
        }
        this.plannerDropStartAtMode = 'drop'
        this.show()
      } catch (error) {
        parseRequestError(error)
      } finally {
        this.loading = false
      }
    },
    async openForTeamDefinition(teamDefinition) {
      this.resetState()
      this.mode = 'teamDefinition'
      this.fixedTeamDefinition = this.normalizeTeamDefinition(teamDefinition)
      const fixedId = this.fixedTeamDefinition?.id || null
      this.selectedTeamDefinitionIds = fixedId ? [fixedId] : []
      this.loading = true

      try {
        await this.loadTeamDefinitions()
        await this.fetchBacklogOrders()
        this.show()
      } catch (error) {
        parseRequestError(error)
      } finally {
        this.loading = false
      }
    },
    show() {
      this.$refs.modal.show()
    },
    hide() {
      this.$refs.modal.hide()
    },
    handleHidden() {
      this.resetState()
      this.$emit('hidden')
    },
    resetState() {
      clearTimeout(this.orderSearchTimeout)
      this.loading = false
      this.saving = false
      this.backlogLoading = false
      this.mode = null
      this.fixedTeamDefinition = null
      this.selectedOrder = null
      this.selectedTeamDefinitionIds = []
      this.assignmentOffsetMinutes = null
      this.assignmentOffsetInput = null
      this.assignmentOffsetValidationError = ''
      this.durationMinutes = null
      this.durationInput = null
      this.durationValidationError = ''
      this.plannerDropContext = null
      this.plannerDropStartAtMode = 'drop'
      this.backlogOrders = []
      this.backlogQuery = {
        limit: BACKLOG_LIMIT,
        page: 1,
      }
      this.backlogTotal = 0
      this.orderSearch = ''
    },
    normalizeTeamDefinition(teamDefinition) {
      if (!teamDefinition || typeof teamDefinition !== 'object') {
        return null
      }

      return {
        id: teamDefinition.id,
        code: teamDefinition.code,
      }
    },
    async loadTeamDefinitions() {
      const response = await teamDefinitionRequests.getTeamDefinitions({
        limit: 250,
        sortField: 'code',
        sortDir: 'asc',
      })

      const responseData = response?.data
      if (Array.isArray(responseData?.items)) {
        this.teamDefinitions = responseData.items
        return
      }

      this.teamDefinitions = Array.isArray(responseData) ? responseData : []
    },
    async fetchBacklogOrders() {
      if (!this.isTeamDefinitionContext) {
        return
      }

      this.backlogLoading = true

      try {
        const response = await orderRequests.getOrders({
          q: this.orderSearch || null,
          statuses: ['received'],
          unplannedOnly: 1,
          limit: this.backlogQuery.limit,
          page: this.backlogQuery.page,
          sortField: 'startAt',
          sortDir: 'asc',
        })

        const items = Array.isArray(response?.data?.items) ? response.data.items : []
        this.backlogOrders = items.filter(item => String(item?.id) !== String(this.selectedOrderId || ''))
        this.backlogTotal = Number(response?.data?.total || 0)
      } catch (error) {
        parseRequestError(error)
      } finally {
        this.backlogLoading = false
      }
    },
    handleOrderSearchInput() {
      clearTimeout(this.orderSearchTimeout)
      this.backlogQuery.page = 1
      this.orderSearchTimeout = setTimeout(() => {
        this.fetchBacklogOrders()
      }, ORDER_SEARCH_DEBOUNCE_MS)
    },
    selectOrder(order) {
      const normalizedOrderId = order?.id ?? order?.orderId ?? null
      if (!order || !normalizedOrderId) {
        return
      }

      const {
        customerName: directCustomerName,
        customerAddress,
        patientInfo,
        patients: directPatients,
      } = order

      const customerName = directCustomerName
        || customerAddress?.customer?.name
        || null

      const patients = directPatients
        || (Array.isArray(patientInfo)
          ? patientInfo.map(p => ({ name: p.name, surname: p.surname }))
          : [])

      this.selectedOrder = {
        ...order,
        id: normalizedOrderId,
        customerName,
        patients,
      }
      if (this.isTeamDefinitionContext) {
        const fixedId = this.fixedTeamDefinition?.id || null
        this.selectedTeamDefinitionIds = fixedId ? [fixedId] : []
      } else if (order?.isFirstAvailablePlanning && Array.isArray(order.plannedTeamDefinitions)) {
        this.selectedTeamDefinitionIds = order.plannedTeamDefinitions.map(td => td.id).filter(Boolean)
      } else {
        const singleId = order?.plannedTeamDefinition?.id || null
        this.selectedTeamDefinitionIds = singleId ? [singleId] : []
      }

      this.assignmentOffsetMinutes = this.resolveAssignmentOffsetMinutes(order)
      this.assignmentOffsetInput = this.formatDurationMinutes(this.assignmentOffsetMinutes)
      this.assignmentOffsetValidationError = ''
      this.durationMinutes = this.resolveDurationMinutes(order)
      this.durationInput = this.formatDurationMinutes(this.durationMinutes)
      this.durationValidationError = ''
      if (this.isTeamDefinitionContext) {
        this.backlogOrders = this.backlogOrders.filter(item => String(item.id) !== String(normalizedOrderId))
      }
    },
    resolveAssignmentOffsetMinutes(order) {
      const assignmentOffsetMinutes = Number(order?.plannedAssignmentOffsetMinutes)

      return Number.isFinite(assignmentOffsetMinutes) && assignmentOffsetMinutes >= 0
        ? assignmentOffsetMinutes
        : null
    },
    resolveDurationMinutes(order) {
      if (order?.plannedDurationMinutes) {
        return Number(order.plannedDurationMinutes)
      }

      return null
    },
    formatDateObjectForPicker(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')

      return `${year}-${month}-${day} ${hours}:${minutes}`
    },
    getBacklogRoutePopoverTarget(order) {
      return `order-planning-route-${order?.id || 'unknown'}`
    },
    getBacklogAddressParts(address) {
      if (!address || typeof address !== 'object') {
        return null
      }

      const alias = address.alias || ''
      const name = address.name || ''
      const addressLine = [address.city, address.address].filter(Boolean).join(', ')

      if (alias || name) {
        return {
          alias,
          name,
          addressLine,
          isAddressPrimary: false,
        }
      }

      if (addressLine) {
        return {
          alias: '',
          name: '',
          addressLine,
          isAddressPrimary: true,
        }
      }

      return null
    },
    formatDurationMinutes(value) {
      return formatPlanningMinutes(value) || null
    },
    handleDurationInput(value) {
      if (typeof value !== 'string') {
        this.durationValidationError = this.$t('order.planning.duration-invalid')
        this.durationInput = value
        this.durationMinutes = null
        return
      }

      const { parsedValue: parsedDuration, normalizedValue } = normalizePlanningTimeInput(value)

      this.durationInput = normalizedValue
      this.durationMinutes = parsedDuration
      this.durationValidationError = parsedDuration
        ? ''
        : this.$t('order.planning.duration-invalid')
    },
    handleAssignmentOffsetInput(value) {
      if (typeof value !== 'string') {
        this.assignmentOffsetValidationError = this.$t('order.planning.duration-invalid')
        this.assignmentOffsetInput = value
        this.assignmentOffsetMinutes = null
        return
      }

      const { parsedValue, normalizedValue } = normalizePlanningTimeInput(value)
      this.assignmentOffsetInput = normalizedValue
      this.assignmentOffsetMinutes = parsedValue
      this.assignmentOffsetValidationError = parsedValue !== null
        ? ''
        : this.$t('order.planning.duration-invalid')
    },
    calculateDurationSelectPosition(dropdownList, component, coordinates) {
      const toggleBounds = component.$refs.toggle.getBoundingClientRect()
      const dropdownHeight = dropdownList.scrollHeight
      const viewportHeight = window.innerHeight
      const spacing = 8
      const availableBelow = viewportHeight - toggleBounds.bottom
      const shouldOpenAbove = availableBelow < Math.min(dropdownHeight, 240)
      const top = shouldOpenAbove
        ? `${window.scrollY + toggleBounds.top - dropdownHeight - spacing}px`
        : coordinates.top
      const { style: dropdownStyle } = dropdownList

      dropdownStyle.width = coordinates.width
      dropdownStyle.left = coordinates.left
      dropdownStyle.top = top
      dropdownStyle.zIndex = '1060'
    },
    async savePlanning() {
      const { selectedOrderId } = this
      if (!this.canSubmit || !selectedOrderId) {
        return
      }

      this.saving = true

      try {
        const planningPayload = this.isFirstAvailable
          ? {
            firstAvailable: true,
            teamDefinitions: this.selectedTeamDefinitionIds.map(Number),
            assignmentOffsetMinutes: Number(this.assignmentOffsetMinutes),
            durationMinutes: Number(this.durationMinutes),
          }
          : {
            teamDefinition: this.selectedTeamDefinitionIds[0] || null,
            assignmentOffsetMinutes: Number(this.assignmentOffsetMinutes),
            durationMinutes: Number(this.durationMinutes),
          }

        let response = null
        const shouldPatchStartAt = this.shouldUseDroppedStartAt

        if (shouldPatchStartAt) {
          const orderPayload = {
            startAt: moment.parseZone(this.plannerDropContext.startAt).format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
            plannedAssignmentOffsetMinutes: Number(this.assignmentOffsetMinutes),
            plannedDurationMinutes: Number(this.durationMinutes),
          }

          await orderRequests.patchOrder(selectedOrderId, orderPayload)
          response = await orderRequests.upsertOrderPlanning(selectedOrderId, planningPayload)
        } else {
          response = await orderRequests.upsertOrderPlanning(selectedOrderId, planningPayload)
        }

        if (window?.toast?.notify?.success) {
          window.toast.notify.success(this.$t('order.planning.notify.updated'))
        }

        this.$emit('planning-updated', {
          orderId: selectedOrderId,
          planning: response?.data || null,
          isFirstAvailable: this.isFirstAvailable,
          teamDefinitionId: this.isFirstAvailable ? null : (this.selectedTeamDefinitionIds[0] || null),
          teamDefinitionIds: this.selectedTeamDefinitionIds.map(Number),
          assignmentOffsetMinutes: Number(this.assignmentOffsetMinutes),
          durationMinutes: Number(this.durationMinutes),
          startAt: this.shouldUseDroppedStartAt ? this.plannerDropContext?.startAt : this.selectedOrder.startAt,
          fromPlannerDrop: Boolean(this.plannerDropContext),
        })
        this.hide()
      } catch (error) {
        parseRequestError(error)
      } finally {
        this.saving = false
      }
    },
    async removePlanning() {
      const { selectedOrderId } = this
      if (!selectedOrderId || !this.canRemovePlanning) {
        return
      }

      this.saving = true

      try {
        await orderRequests.deleteOrderPlanning(selectedOrderId)
        if (window?.toast?.notify?.success) {
          window.toast.notify.success(this.$t('order.planning.notify.updated'))
        }

        this.$emit('planning-updated', {
          orderId: selectedOrderId,
          planning: null,
        })
        this.hide()
      } catch (error) {
        parseRequestError(error)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.planning-order-number-badge {
  min-width: 7.5rem;
  padding: 0.5rem 0.75rem;
}

.planning-route-button {
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
}

.planning-route-popover {
  max-width: 18rem;
}

.planning-route-popover__section + .planning-route-popover__section {
  margin-top: 0.5rem;
}

.planning-route-popover__label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6e6b7b;
  text-transform: uppercase;
}

.planning-route-popover__value {
  margin-top: 0.25rem;
  line-height: 1.4;
}

.planning-flags-column,
.planning-route-column {
  width: 1%;
  white-space: nowrap;
}
</style>
